import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { AppType } from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	EditConnectWiseAppViewModel,
	saveConnectWiseApp,
	SaveConnectWiseAppModel
} from "client/Api/Apps/EditApp/saveApp";

export type EditConnectWiseAppProps = {
	initialSettings: EditAppSettings<EditConnectWiseAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditConnectWiseApp = forwardRef(
	(
		props: EditConnectWiseAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveConnectWiseAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveConnectWiseAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveConnectWiseAppModel>(
			formHandler,
			saveConnectWiseApp,
			AppType.ConnectWise
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);
EditConnectWiseApp.displayName = "EditConnectWiseApp";

const validation = createValidationConfiguration<SaveConnectWiseAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function convertToSaveModel(
	initialSettings: EditAppSettings<EditConnectWiseAppViewModel>
): SaveConnectWiseAppModel {
	const {
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow
	};
}
